/**
 * Named camera angles for the 3D studios.
 *
 * Deliberately free of three.js and R3F: the recipe schema declares
 * `cameraAngle` from this table, and the schema module is imported by the plan
 * compiler and its tests, which must not drag a WebGL stack behind them.
 */

export type CamView = { pos: [number, number, number]; target: [number, number, number] };

/**
 * Lens presets, in real focal lengths.
 *
 * Lives here rather than beside the studio that renders them: the recipe schema
 * needs this list to make the lens settable, and the schema module is imported
 * by the plan compiler and its tests, which must not drag a WebGL stack behind
 * them. Same reason the angle table is here.
 */
export const LENS_PRESETS: { mm: number; label: string }[] = [
    { mm: 24, label: 'Wide' },
    { mm: 35, label: 'Room' },
    { mm: 50, label: 'Natural' },
    { mm: 85, label: 'Portrait' },
    { mm: 135, label: 'Tele' },
    { mm: 200, label: 'Long' },
];

/** The lens the shot is taken through, as the camera itself reports it. */
export interface LensFraming {
    /** Vertical field of view in degrees (three's `PerspectiveCamera.fov`). */
    fovDeg: number;
    /** Frame aspect (width / height) — `PerspectiveCamera.aspect`. */
    aspect: number;
}

/** What the studio uses when nobody says otherwise: its default 50mm lens on a
 *  square frame, as three resolves it (filmGauge 35 → fov ≈ 38.6°). */
export const DEFAULT_LENS: LensFraming = { fovDeg: 38.6, aspect: 1 };

/**
 * Angles a vendor (or the agent) can ask for by name.
 *
 * The studio's camera is a position and a target, which is the right shape to
 * pose by hand and the wrong shape to ask for in words. "A low three-quarter
 * view" has to become a `CamView` somewhere, and it has to be the *same*
 * somewhere for every caller — the agent setting it, the studio restoring it,
 * and the text written back after a capture all have to agree, or the node
 * will say one thing and show another.
 *
 * Azimuth is degrees around the subject (0 = front), elevation is degrees above
 * the horizon, and `tightness` is how full the frame is — 1 means the model's
 * bounding sphere exactly touches the narrower edge, above 1 leaves air around
 * it, below 1 crops in deliberately. It is not a distance: the distance that
 * achieves it depends on the lens and the model, and is worked out below.
 */
export const NAMED_CAMERA_ANGLES: Record<string, {
    azimuth: number; elevation: number; tightness: number; label: string;
}> = {
    front: { azimuth: 0, elevation: 8, tightness: 1.15, label: 'Straight on, level with the piece' },
    'three-quarter-left': { azimuth: -35, elevation: 12, tightness: 1.15, label: 'Classic three-quarter from the left' },
    'three-quarter-right': { azimuth: 35, elevation: 12, tightness: 1.15, label: 'Classic three-quarter from the right' },
    side: { azimuth: 90, elevation: 8, tightness: 1.15, label: 'Full profile from the side' },
    back: { azimuth: 180, elevation: 10, tightness: 1.15, label: 'From behind' },
    'low-angle': { azimuth: -25, elevation: -5, tightness: 1.2, label: 'Low, looking slightly up — makes a piece look larger' },
    'eye-level': { azimuth: -20, elevation: 5, tightness: 1.15, label: 'Eye level with the piece' },
    'high-angle': { azimuth: -25, elevation: 35, tightness: 1.2, label: 'Looking down at the piece and the floor around it' },
    'top-down': { azimuth: 0, elevation: 80, tightness: 1.2, label: 'Almost directly overhead — flat-lay' },
    'close-detail': { azimuth: -30, elevation: 10, tightness: 0.6, label: 'Close in on the material and joinery' },
};

export type NamedCameraAngle = keyof typeof NAMED_CAMERA_ANGLES;

/**
 * How far back the camera has to sit for a sphere of `radius` to fill the frame
 * to `tightness` through this lens.
 *
 * The narrower of the two half-angles wins, because that is the edge the model
 * runs out of room against first — fitting to the vertical alone crops the sides
 * of anything on a wide frame.
 */
export function framingDistance(radius: number, tightness: number, lens: LensFraming = DEFAULT_LENS): number {
    const halfV = (Math.max(1, lens.fovDeg) * Math.PI) / 360;
    const halfH = Math.atan(Math.tan(halfV) * Math.max(0.05, lens.aspect));
    const half = Math.min(halfV, halfH);
    return (Math.max(radius, 0.001) / Math.sin(half)) * tightness;
}

/**
 * Resolve a named angle to a camera view around a model.
 *
 * `center` and `radius` come from the model's own bounding sphere and `lens`
 * from the camera that will take the shot, so the same name frames a bathtub
 * and a doorknob alike, on any lens. Fixed distances could not: a bathtub shot
 * from "2.6 radii" on a 50mm lens overflows the frame on two sides, and the
 * same number on a wide lens leaves the piece a speck.
 *
 * Returns null for a name we do not know, so a caller keeps whatever the vendor
 * last framed rather than pointing the camera at empty space.
 */
/**
 * Resolve a named angle to a camera view INSIDE a room.
 *
 * `resolveNamedCameraAngle` frames a bounding sphere from outside, which is
 * right for a sofa and wrong for a room: backing off far enough to fit the
 * whole shell in frame produces a diorama — the room floating in grid space —
 * when the reference the vendor attached is a person standing in a kitchen.
 * An interior shot is a different camera model: standing height, near the
 * room's open side, looking toward the back wall so the floor fills the lower
 * half of the frame.
 *
 * The room is centred on the origin: floor x ∈ [−W/2, W/2], z ∈ [−D/2, D/2],
 * back wall at −D/2, the front at +D/2 open. Azimuths are clamped to the
 * front hemisphere — "side" means from the open corner, because from beyond
 * ±70° the shot is through a wall.
 *
 * Returns null for a name it does not know, same contract as the model
 * resolver: the caller keeps the vendor's framing rather than guessing.
 */
/** A camera measured from a reference photograph, rather than named. */
export interface MeasuredCamera {
    /** Height above the floor, metres. */
    heightM?: number;
    /** Downward tilt, degrees. 0 = level. */
    pitchDeg: number;
    /** Sideways lean, degrees. Usually ~0. */
    rollDeg?: number;
    /** 35mm-equivalent focal length. 0 or absent = could not be read. */
    lensMm?: number;
    /**
     * Vertical field of view of the reference, in degrees.
     *
     * Carried instead of relying on `lensMm`, because millimetres are not a
     * field of view until you say what film they are on. The measurement
     * converts using a 24mm frame height; three.js converts back using
     * filmGauge / max(aspect, 1). On a portrait reference those two disagree
     * by up to twenty degrees of view — measured, not guessed — which is the
     * whole of the "the zoom is never the same" complaint.
     */
    fovDegV?: number;
    /**
     * Bearing of the reference floor's tile grid, degrees, 0-90.
     *
     * Not part of the camera, but measured with it and useless apart from it:
     * a correct camera over a grid laid the wrong way still shows the joints
     * running somewhere the reference's do not.
     */
    gridBearingDeg?: number;
    /**
     * The reference's own width/height.
     *
     * A field of view is vertical, so it reproduces a framing only on a frame
     * of the same shape. On a wider canvas the same 67 degrees shows far more
     * to the sides, and the shot reads as taken from further back — the angle
     * right, the framing wrong.
     */
    aspect?: number;
}

/** A pose the studio can adopt, with the parts lookAt alone cannot carry. */
export type MeasuredView = CamView & {
    /** Sideways lean. lookAt always levels the camera, so this is applied after.
     *  Absent for a named angle, which was never measured off anything. */
    rollDeg?: number;
    /** Vertical field of view to set directly, bypassing the lens round trip. */
    fovDegV?: number;
};

/** Read the JSON a plan wrote, without trusting it. */
export function parseMeasuredCamera(raw: unknown): MeasuredCamera | null {
    if (!raw) return null;
    let obj: any = raw;
    if (typeof raw === 'string') {
        try { obj = JSON.parse(raw); } catch { return null; }
    }
    if (!obj || typeof obj !== 'object') return null;
    const pitch = Number(obj.pitchDeg);
    if (!Number.isFinite(pitch)) return null;
    const height = Number(obj.heightM);
    const lens = Number(obj.lensMm);
    const fov = Number(obj.fovDegV);
    return {
        // Beyond these a "measurement" is a failed estimate, and posing to it
        // would put the camera through the floor or in orbit.
        pitchDeg: Math.max(-20, Math.min(89, pitch)),
        rollDeg: Number.isFinite(Number(obj.rollDeg)) ? Number(obj.rollDeg) : 0,
        heightM: Number.isFinite(height) && height > 0.1 && height < 8 ? height : undefined,
        lensMm: Number.isFinite(lens) && lens >= 10 && lens <= 200 ? lens : undefined,
        // Anything outside this is a failed estimate, not a lens.
        fovDegV: Number.isFinite(fov) && fov > 5 && fov < 140 ? fov : undefined,
        // Beyond this it is not a photograph's shape.
        aspect: Number.isFinite(Number(obj.aspect)) && Number(obj.aspect) > 0.1 && Number(obj.aspect) < 10
            ? Number(obj.aspect) : undefined,
        gridBearingDeg: Number.isFinite(Number(obj.gridBearingDeg))
            ? ((Number(obj.gridBearingDeg) % 90) + 90) % 90
            : undefined,
    };
}

/**
 * Place the studio camera where a measured one stood, inside a room.
 *
 * The measurement fixes height and tilt; where to stand follows from them.
 * The camera looks at the point on the floor its own tilt points at — which
 * is what "standing here, tilted this far down" means — so the horizon lands
 * where the reference put it, and the floor fills the same share of frame.
 */
export function resolveMeasuredRoomCamera(
    cam: MeasuredCamera,
    floorW: number,
    floorD: number,
): MeasuredView {
    const D = Math.max(1, floorD);
    const y = cam.heightM && cam.heightM > 0.1 ? cam.heightM : 1.5;
    const pitchDeg = Math.max(0, cam.pitchDeg);
    const pitch = (pitchDeg * Math.PI) / 180;

    // Stand at the room's open edge, as an interior shot does.
    const z = D / 2 + 0.6;

    // Aim by DIRECTION, not by a point on the floor.
    //
    // The target used to be where the optical axis meets the floor, clamped so
    // it stayed inside the room. But a shallow tilt reaches a long way before
    // it lands, so the clamp bit exactly when the shot was nearly level — and
    // a camera aimed at a nearer point is a camera tilted further down. A
    // reference measured at 5.7 degrees was posed at 7.9. The clamp was
    // silently editing the measurement it was given.
    //
    // A direction cannot be clamped into a different angle. The distance along
    // it only has to be positive.
    const reach = Math.max(2, D * 1.2);
    return {
        pos: [0, y, z],
        target: [0, y - reach * Math.sin(pitch), z - reach * Math.cos(pitch)],
        rollDeg: cam.rollDeg || 0,
        fovDegV: cam.fovDegV,
    };
}

export function resolveRoomInteriorAngle(
    name: string | undefined,
    floorW: number,
    floorD: number,
    wallH: number,
    lens: LensFraming = DEFAULT_LENS,
): CamView | null {
    const spec = name ? NAMED_CAMERA_ANGLES[name] : undefined;
    if (!spec) return null;

    const W = Math.max(1, floorW);
    const D = Math.max(1, floorD);
    const H = Math.max(2, wallH);

    // Overhead family: straight down, high enough that the floor fits the
    // frame through this lens — the flat-lay of the room.
    if (spec.elevation >= 60) {
        const halfV = (Math.max(1, lens.fovDeg) * Math.PI) / 360;
        const halfH = Math.atan(Math.tan(halfV) * Math.max(0.05, lens.aspect));
        const height = Math.max(
            2.2,
            (D / 2) / Math.tan(halfV) * 1.1,
            (W / 2) / Math.tan(halfH) * 1.1,
        );
        // A hair of z so the up-vector never degenerates looking straight down.
        return { pos: [0, height, 0.01], target: [0, 0, 0] };
    }

    // Standing shots. The camera sits just past the open edge — with no wall
    // on the front side this reads as standing in the room's doorway, which is
    // the composition of every kitchen reference: back wall up top, a sweep of
    // floor below.
    const azDeg = Math.max(-70, Math.min(70, spec.azimuth));
    const az = (azDeg * Math.PI) / 180;
    const r = D / 2 + (spec.tightness < 1 ? 0.2 : 0.8);

    const heightFor = () => {
        if (spec.elevation < 0) return 0.7;              // low: near the floor, looking up the room
        if (spec.elevation >= 25) return Math.min(H * 0.95, 2.5); // high: under the ceiling line
        return 1.5;                                       // eye level: a standing person
    };
    const y = heightFor();

    // The look target leans into the room: a third of the way to the back
    // wall, below eye height, so the floor takes the lower half of the frame.
    const target: [number, number, number] = spec.tightness < 1
        ? [0, 0.05, r * 0.35 - 1.2]                       // close-detail: down at the material itself
        : [0, Math.min(0.9, y * 0.55), -D * 0.18];

    return {
        pos: [Math.sin(az) * r, y, Math.cos(az) * r],
        target,
    };
}

export function resolveNamedCameraAngle(
    name: string | undefined,
    center: [number, number, number] = [0, 0.5, 0],
    radius = 1,
    lens: LensFraming = DEFAULT_LENS,
    opts?: { minElevationDeg?: number },
): CamView | null {
    const spec = name ? NAMED_CAMERA_ANGLES[name] : undefined;
    if (!spec) return null;

    const az = (spec.azimuth * Math.PI) / 180;
    const elDeg = Math.max(spec.elevation, opts?.minElevationDeg ?? -90);
    const el = (elDeg * Math.PI) / 180;
    const distance = framingDistance(radius, spec.tightness, lens);
    const horizontal = Math.cos(el) * distance;

    return {
        pos: [
            center[0] + Math.sin(az) * horizontal,
            center[1] + Math.sin(el) * distance,
            center[2] + Math.cos(az) * horizontal,
        ],
        target: center,
    };
}


/**
 * Elevation floor for a flat product (a rug) at a named angle.
 *
 * The default framings ("eye level", the three-quarter views) sit 5-12 degrees
 * up, which turns a rug into a sliver — so those are raised to 30, the view of
 * someone standing over it. An angle chosen on purpose is kept: "low angle" or
 * "close on the detail" on a rug means grazing the pile, and it only needs to
 * stay above the floor.
 */
export function flatProductElevation(angle: string | undefined, flat: boolean): { minElevationDeg: number } | undefined {
    if (!flat) return undefined;
    const defaults = new Set(['eye-level', 'three-quarter-left', 'three-quarter-right']);
    if (angle && defaults.has(angle)) return { minElevationDeg: 30 };
    // Asked-for low and close shots stay low, but not so low that the rug is a
    // strip: at 6 degrees a 2 m rug is a thin band however close the camera
    // gets (posed-camera harness, 11 Oct 2026).
    if (angle === 'low-angle') return { minElevationDeg: 12 };
    if (angle === 'close-detail') return { minElevationDeg: 22 };
    return { minElevationDeg: 6 };
}
